/* eslint-disable */
import createIcon from './icon.js';
import config from '../config.json';
import { csrfToken } from "@/utils";

const ClASS_NAME = 'ds-header__navbar__nav';
export const LINKS_TYPE = {
  DROPDOWN_NAV: 0,
  STATIC_NAV: 1,
};

function createPill({ value, className }) {
  const element = document.createElement('div');
  element.classList.add(className);
  element.appendChild(document.createTextNode(value));
  if (value === 0) {
    element.classList.add(`${className}--empty`);
  }
  return element;
}

// This wrapper is for flexbox positioning of icon and text
function createLeftLinkWrapper({ parentClassName, label, options }) {
  const leftWrapper = document.createElement('div');
  leftWrapper.classList.add(`${parentClassName}__left_wrapper`)

  if (options.icon) {
    leftWrapper.appendChild(createIcon({
      iconName: options.icon,
      className: `${parentClassName}__icon`,
      additionalClassName: `${parentClassName}__icon--small`,
    }));
  }
  leftWrapper.appendChild(document.createTextNode(label));

  if (options.suffix) {
    const suffix = document.createElement('span');
    suffix.classList.add(`${parentClassName}__suffix`);
    suffix.appendChild(document.createTextNode(options.suffix));
    leftWrapper.appendChild(suffix);
  }

  return leftWrapper;
}

/// purge empty element_value from tracking data
export function buildTrackedElementValue({ tracking, value }) {
  const elementValue = tracking.elementValue ?? (typeof value === 'number' ? value : null);

  if (elementValue === null || elementValue === '') {
    return {};
  }

  return { element_value: elementValue };
}

export function createLink({ url, label, options = {}, className }) {
  const element = document.createElement('a');
  element.classList.add(className);
  element.href = url;

  if (options.mobileOnly) {
    element.classList.add(`${className}--mobile-only`);
  }

  if (options.desktopOnly) {
    element.classList.add(`${className}--desktop-only`);
  }

  element.appendChild(createLeftLinkWrapper({
    parentClassName: className,
    label,
    options,
  }));

  if (typeof options.value === 'number') {
    element.appendChild(createPill({
      value: options.value,
      className: `${className}__pill`,
    }));
  }

  if (options.bold) {
    element.classList.add(`${className}--bold`);
  }

  if (options.new) {
    element.classList.add(`${className}--new`);
  }

  // @todo TM-7783 remove after the business account MVP is validated
  if (options.button) {
    element.classList.add(`${className}--button`);

    if (options.buttonStyle) {
      element.classList.add(`${className}--button-${options.buttonStyle}`);
    }

    if (options.buttonLabelOnly) {
      element.classList.add(`${className}--button-label-only`);
    }
  }

  // @todo TM-7783 remove after the business account MVP is validated
  if (options.trailingIcon) {
    element.appendChild(createIcon({
      iconName: options.trailingIcon,
      className: `${className}__trailing-icon`,
    }));
  }

  if (options.id) {
    element.id = options.id;
  }

  // Marks a row that drills into a sub-panel of the mobile menu. The anchor keeps its href so the
  // row still leads somewhere sensible before menuPanels.js has attached its handler.
  if (options.panelTarget) {
    element.dataset.panelTarget = options.panelTarget;
  }

  if (options.current) {
    element.classList.add(`${className}--current`);
    element.setAttribute('aria-current', 'page');
  }

  if (options.chevron) {
    const chevron = document.createElement('span');
    chevron.classList.add(`${className}__chevron`);
    element.appendChild(chevron);
  }

  if (options.onclick && Array.isArray(options.onclick)) {
    if (options.onclick.includes('publishLogoutEvent')) {
      $.publish('loggingOut');
    }
  }
  // A row that opens a sub-panel is not a destination, so it reports nothing: its tracking data
  // belongs to the page it stands for, and firing it here would count a panel that was opened as a
  // page that was visited. DropdownHandler skips the same rows for the same reason.
  if (options?.tracking && !options.panelTarget) {
    element.addEventListener('click', () => {
      App.track.event('navbar_interaction', {
        section: options.tracking.section,
        element: options.tracking.element,
        ...buildTrackedElementValue(options),
        interaction: 'click',
      });
    });
  }

  return element;
}

// A CTA label written for a wide header button does not fit the 213px dropdown, so a link
// may offer a shorter one for when it collapses in behind the avatar.
// @todo TM-7783 remove after the business account MVP is validated
function resolveLabel({ label, options = {} }, linksType) {
  const useDropdownLabel = linksType === LINKS_TYPE.DROPDOWN_NAV && options.dropdownLabel;

  return useDropdownLabel ? options.dropdownLabel : label;
}

function createLinkList({ links, parentClassName, linksType }) {
  const ul = document.createElement('ul');
  const separatorClass = `${parentClassName}__link-separator`;
  const sectionTitleClass = `${parentClassName}__section-title`;

  links.forEach(link => {
    const li = document.createElement('li');
    let shouldCreateLink = link.name !== config.separatorName && link.name !== config.sectionTitleName;

    if (link.featureFlag && window.location.href.indexOf(link.featureFlag + '=1') < 0) {
      shouldCreateLink = false;
    }

    if (shouldCreateLink) {
      li.appendChild(createLink({
        ...link,
        label: resolveLabel(link, linksType),
        className: `${ClASS_NAME}__link`,
      }));
      ul.appendChild(li);
      return;
    }

    const shouldCreateSeparator = link.name === config.separatorName && linksType === LINKS_TYPE.DROPDOWN_NAV;
    if (shouldCreateSeparator) {
      li.classList.add(separatorClass);
      ul.appendChild(li);
    }

    const shouldCreateSectionTitle = link.name === config.sectionTitleName && linksType === LINKS_TYPE.DROPDOWN_NAV;
    if (shouldCreateSectionTitle) {
      li.classList.add(sectionTitleClass);
      li.appendChild(document.createTextNode(link.label));
      ul.appendChild(li);
    }
  });
  return ul;
}

export function createLinks({ links, className, linksType }) {
  const nav = document.createElement('nav');
  nav.classList.add(ClASS_NAME);
  if (className) {
    nav.classList.add(className);
  }

  nav.appendChild(createLinkList({
    links,
    parentClassName: ClASS_NAME,
    linksType,
  }));
  return nav;
}

export function switchAccounts(event) {
  const radioInputs = document.querySelectorAll('input[name="accountType"]');
  if (radioInputs.length > 0 && radioInputs[0].disabled) {
    return false;
  }

  let section = 'navbar_customer';
  let elementValue = 'switch_to_manager';
  if (event.target.id === 'switchToUserAccount') {
    section = 'navbar_manager';
    elementValue = 'switch_to_customer';
  }

  App.track.event('navbar_interaction', {
    section,
    element: 'switch_to',
    element_value: elementValue,
    interaction: 'click',
  });

  $.ajax({
    url: '/switch-accounts',
    type: 'POST',
    data: {
      '_token': csrfToken(),
    },
    beforeSend: function () {
      const radioInputs = document.querySelectorAll('input[name="accountType"]');
      radioInputs.forEach(radio => {
        radio.disabled = true;
      });
    },
    success: function ({ redirectUrl }) {
      if (window.location.href !== redirectUrl) {
        window.location.href = redirectUrl;
      }
    },
    error: function (err) {
      console.error(err);
      alert('Something went wrong during account switching. Please try again later.');
      Rollbar.critical('Error switching accounts');
    },
  });
}

export function createSwitchAccountButtons({ user }) {
  const html = `<div class="navbar-switch-accounts-container">
      <p class="navbar-switch-accounts-title">Switch to</p>
      <div class="navbar-switch-accounts-options">
          <div id="switchToUserAccount" class="navbar-switch-accounts-option">
              <div class="ds-radio navbar-switch-accounts-radio">
                  <input type="radio" name="accountType" value="user" class="ds-radio-input" ${user.type === 'user' && 'checked readonly'} />
                  <div class="ds-radio-indicator navbar-switch-accounts-indicator"></div>
                  <span class="ds-radio-text">User Account</span>
              </div>
          </div>

          <div id="switchToManagerAccount" class="navbar-switch-accounts-option">
              <div class="ds-radio navbar-switch-accounts-radio">
                  <input type="radio" name="accountType" value="user" class="ds-radio-input" ${user.type === 'manager' && 'checked readonly'} />
                  <div class="ds-radio-indicator navbar-switch-accounts-indicator"></div>
                  <span class="ds-radio-text">Venue Account</span>
              </div>
          </div>
      </div>
  </div>`;

  const div = document.createElement('div');
  div.innerHTML = html;
  return div;
}
